/**
 * Log severity levels (numeric so they can be compared/threshold-filtered).
 * `Silent` disables all output.
 */
export enum LogLevel {
  Trace = 10,
  Debug = 20,
  Info = 30,
  Warn = 40,
  Error = 50,
  Critical = 60,
  Silent = 100,
}

/** Semantic category of a log event — powers structured lifecycle/UI tracking. */
export type LogCategory =
  | 'general'
  | 'lifecycle'
  | 'route'
  | 'theme'
  | 'language'
  | 'translation'
  | 'home'
  | 'ui-event'
  | 'user-action';

/** Arbitrary structured payload attached to a log event (strongly typed, no `any`). */
export type LogContext = Record<string, unknown>;

/** A single, fully-described log event. */
export interface LogEvent {
  level: LogLevel;
  category: LogCategory;
  message: string;
  /** Emitting unit (component/service/facade name). */
  source?: string;
  context?: LogContext;
  /** ISO timestamp. */
  timestamp: string;
  /** Stable id for one app session — ties related events together. */
  correlationId: string;
  /** Captured only for Error/Critical in development. */
  stack?: string;
}

/**
 * A log sink receives finalised events. The default sink in production is a
 * no-op (no console). Swap it (via the LOG_SINK token) to ship events to a
 * remote collector, BroadcastChannel, etc.
 */
export interface LogSink {
  write(event: LogEvent): void;
}
